import axios from "axios";
import { tMsg } from "../../ToastNotifications";
import { useState } from "react";
import { API_DOMAIN } from "../../../api/apiAgent";
import { useAtomValue } from "jotai";
import { hostDataAtom } from "../../../atoms";
import { pause } from "../../../utils";

// TODO: Should get these from the backend?
// const bucket = "lightwell-test";
// const region = "ap-southeast-2";
const chunkSize = 1024 * 1024 * 5; // TODO: Should get this from config later, needs to be minimum 5MB
const minHeightAndWidthPx = 150;

type MultiPartRequest = {
  MultiPartUploadId: string;
  URLs: string[];
};

type UploadPart = {
  ETag: string;
  PartNumber: number;
};

export const getThumbFilename = (fileName: string): string => {
  const removeWebAddress = fileName.substring(fileName.lastIndexOf("/") + 1);
  const removeExt = removeWebAddress.substring(
    0,
    removeWebAddress.lastIndexOf(".")
  );
  const replacePlusWithSpace = removeExt.replaceAll("+", " ");
  return `${replacePlusWithSpace}_thumb.png`;
};

const isVideo = (fileName: string) => {
  // TODO: Load `supportedVideoTypes` from backend?
  const supportedVideoTypes = [".mp4"];
  const fileNameParts = fileName.split(".");
  return supportedVideoTypes.includes(
    `.${fileNameParts[fileNameParts.length - 1]}`
  );
};

export const getStillVideoFrameFromURL = async (
  url: string,
  thumbFilename: string,
  setThumbFile: (file: File | null) => void
) => {
  console.log("getStillVideoFrameFromURL - url", url);
  try {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.src = url;
    video.volume = 0;
    video.play().then(async () => {
      await pause(500);
      const isPortrait = video.videoHeight > video.videoWidth;
      const width = isPortrait
        ? (video.videoWidth * 240) / video.videoHeight
        : 240;
      const height = isPortrait
        ? 240
        : (video.videoHeight * 240) / video.videoWidth;
      // FIX: Above calculation can sometime results in very low resolutions (< 50px), the section below re-adjusts that
      const widthMinMP =
        width < minHeightAndWidthPx ? minHeightAndWidthPx / width : 1;
      const heightMinMP =
        height * widthMinMP < minHeightAndWidthPx
          ? minHeightAndWidthPx / (height * widthMinMP)
          : 1;
      const canvas = document.createElement("canvas");
      canvas.width = width * widthMinMP * heightMinMP;
      canvas.height = height * widthMinMP * heightMinMP;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      video.pause();
      video.remove();
      // setDataURL(canvas.toDataURL("image/png"));
      canvas.toBlob((blob) => {
        const isBlob = blob || "";
        setThumbFile(new File([isBlob], thumbFilename));
        console.log("Generated thumb: ", thumbFilename);
      }, "image/png");
    });
  } catch (err) {
    console.log("Error generating thumb", err);
    setThumbFile(null);
  }
};

export const getImageThumbnailFromURL = async (
  url: string,
  thumbFilename: string,
  setThumbFile: (file: File) => void,
  useCrossOrigin?: boolean
) => {
  try {
    pause(500);
    const image = new Image();
    if (useCrossOrigin) image.crossOrigin = "anonymous";
    image.onload = () => {
      const width =
        image.height < image.width ? (image.width * 320) / image.height : 320;
      const height =
        image.height < image.width ? 320 : (image.height * 320) / image.width;
      // FIX: Above calculation can sometime results in very low resolutions (< 50px), the section below re-adjusts that
      const widthMinMP =
        width < minHeightAndWidthPx ? minHeightAndWidthPx / width : 1;
      const heightMinMP =
        height * widthMinMP < minHeightAndWidthPx
          ? minHeightAndWidthPx / (height * widthMinMP)
          : 1;
      const canvas = document.createElement("canvas");
      canvas.width = width * widthMinMP * heightMinMP;
      canvas.height = height * widthMinMP * heightMinMP;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(image, 0, 0, width, height);
      // setDataURL(canvas.toDataURL("image/png"));
      canvas.toBlob((blob) => {
        const isBlob = blob || "";
        setThumbFile(new File([isBlob], thumbFilename));
      }, "image/png");
    };
    image.src = url;
  } catch (err) {
    console.log("Error generating thumb", err);
  }
};

/**
 * Given some files, return (presigned) MultiPartRequest(s) for uploading them
 * @param files
 * @returns
 */
export const getPresignedMultiPartUploadFromFiles = (
  files: File[],
  bucket: string
) => {
  return new Promise<MultiPartRequest[]>((resolve, reject) => {
    const getPresignedPromises: Array<Promise<MultiPartRequest>> = [];

    files.forEach((f) => {
      const fileParts = f.name.split(".");
      const fileExt = f.name.split(".")[fileParts.length - 1];

      // Get presigned URLs and MultiPartUploadId for each file
      getPresignedPromises.push(
        new Promise((resolve, reject) => {
          const resp: MultiPartRequest = {
            MultiPartUploadId: "",
            URLs: [],
          };
          if (f.name === "") {
            resolve(resp);
          }
          const fetchArgs = {
            method: "PUT",
            body: JSON.stringify({
              bucket: bucket,
              key: f.name,
              size: f.size,
              chunkSize: chunkSize,
            }),
            headers: new Headers({
              // TODO: Make this stricter
              "Content-Type": isVideo(f.name)
                ? "video/" + fileExt
                : "image/" + fileExt,
            }),
          };

          const response = axios
            .put(
              `${API_DOMAIN}/s3/presigned_url/`,
              {
                bucket: bucket,
                key: f.name,
                size: f.size,
                chunkSize: chunkSize,
              },
              {
                withCredentials: true,
                headers: {
                  // TODO: Make this stricter
                  "Content-Type": isVideo(f.name)
                    ? "video/" + fileExt
                    : "image/" + fileExt,
                },
              }
            )
            .then((resp) => {
              resolve(resp.data);
            })
            .catch((err) => reject(err));
        })
      );
    });

    // Resolve promises and return MultiPartRequest(s)
    Promise.all(getPresignedPromises)
      .then((data) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Upload the given file
 * @param file
 */
export const uploadFile = (
  file: File,
  onUploadFinished: (fl: string, file?: File) => void,
  bucket: string,
  region: string,
  updateProgress?: (fl: number) => void
) => {
  const files: File[] = file ? [file] : [];

  getPresignedMultiPartUploadFromFiles(files, bucket)
    .then((uploadRequests) => {
      // Do each uploadRequest
      uploadRequests.forEach((req, index) => {
        if (req.URLs.length === 0) {
          return;
        }

        // Find the corresponding file for this uploadRequest
        const file = files[index];

        // Upload each file part of max size `chunkSize`, use partsLeft to track/update upload progress (in chunks)
        let partsLeft = req.URLs.length;
        // console.log("pre request");
        const partUploads = req.URLs.map((u, i) => {
          const chunkStart = i * chunkSize;
          let chunkEnd = chunkStart + chunkSize;
          if (chunkEnd > file.size) {
            chunkEnd = file.size;
          }

          //PUT this chunk of the file on S3
          return axios
            .put(u, file.slice(chunkStart, chunkEnd))
            .then((resp) => {
              if (resp.status === 200) {
                partsLeft--;
                const remaining = Math.round(
                  (1 - partsLeft / partUploads.length) * 100
                );
                console.log(`${remaining}% Completed`);
                if (updateProgress) updateProgress(remaining);
              }
              return resp;
            })
            .catch((err) => {
              console.warn(err);
              tMsg.error("Error uploading file");
              return err;
            });
        });

        // Resolve all promises then, construct `uploadParts` to tell S3 which "chunks" belong to this file
        Promise.all(partUploads)
          .then((results) => {
            const uploadParts: UploadPart[] = [];
            results.forEach((part, index) => {
              uploadParts.push({
                ETag: part.headers.get("etag"),
                PartNumber: index + 1,
              });
            });
            return uploadParts;
          })
          .then((uploadedParts) => {
            // Make request to the backend to `completeMultipartUpload`
            const body = {
              Bucket: bucket,
              Key: file.name,
              UploadId: req.MultiPartUploadId,
              MultipartUpload: uploadedParts,
              ExpectedFileSize: file.size,
            };

            /* Last fetch to complete upload
            fetch("http://localhost:8080/s3/completeMultipartUpload", {
              method: "PUT",
              body: JSON.stringify(body),
              mode: "cors",
            });
            */

            // const response = axios.put(
            //   `${API_DOMAIN}/s3/completeMultipartUpload/`,
            //   body,
            //   { withCredentials: true }
            // );

            const location =
              `https://${bucket}.s3.${region}.amazonaws.com/${file.name}`.replaceAll(
                " ",
                "+"
              );
            // onUploadFinished(location, file);

            uploadParts(
              `${API_DOMAIN}/s3/completeMultipartUpload/`,
              body,
              () => {
                onUploadFinished(location, file);
                tMsg.info("Upload complete");
              }
            );
          })
          .catch((err) => {
            console.warn(err);
            tMsg.error("Error uploading file");
            return err;
          });
      });
    })
    .finally(() => {
      // TODO: This is where we'd submit the body to create/update an item
      console.log("Upload complete");
    });
};

// Had to extract this for async loading
const uploadParts = async (url: string, body: any, onFinished: () => void) => {
  try {
    const response = await axios.put(url, body, {
      withCredentials: true,
    });
    if (response) onFinished();
  } catch (err) {
    console.warn(err);
    tMsg.error("Error uploading file");
  }
};
